import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { admin_cse, admin_csm } from "@/config/roles";
import { Button2 } from "@/ui2/atoms/Button";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { EmptyTable } from "@/ui2/molecules/(table)/EmptyTable";
import { isAuthorized } from "@/utils/authToken";
import type { Note, UUID } from "@/core/types";
import { showAlertWithAction } from "@/redux/actions/alerts";
import { useCosignSupervisor } from "@/features/cosignature/hooks/useCosignSupervisor";
import { useNotesSummary } from "@/features/notes/(v2)/api/queries/useNotesSummary";

import { filterNotesByNoteType } from "./(filters)/helpers/filter-notes";
import { NewNoteModal } from "./(filters)/NewNoteModal";
import { NoteFilters } from "./(filters)/NoteFilters";
import { NoteList } from "./(list)/NoteList";
import { NoteListSkeletons } from "./(list)/components/NoteListSkeletons";
import { sortNotesBySessionDate } from "./(filters)/helpers/sort-notes";
import { useNotesFilter } from "./hooks/useNotesFilter";
import { useNotesSelection } from "./hooks/useNotesSelection";

const selectNotes = (data: Note[]) => filterNotesByNoteType(sortNotesBySessionDate(data));

export function MemberProfileNotes() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { patientId } = useParams<{ patientId: UUID }>();

  const { data: notes, isLoading } = useNotesSummary({ patientId, select: selectNotes });

  const { isSupervisor } = useCosignSupervisor();
  const viewAs = isSupervisor ? "supervisor" : "provider";

  const isAuthorizedToCreateNotes =
    isAuthorized("provider:behavioral") ||
    isAuthorized("provider:prescribe") ||
    isAuthorized(admin_cse) ||
    isAuthorized("provider:peer") ||
    isAuthorized("provider:case_manager");

  const isAuthorizedToExportNotes = isAuthorized(admin_cse) || isAuthorized(admin_csm);

  const {
    clearAll,
    dateEnd,
    dateStart,
    filteredNotes,
    isDirty,
    search,
    setDateEnd,
    setDateStart,
    setNoteType,
    setSearch,
  } = useNotesFilter(notes);

  const { isAllSelected, toggleAll, toggleNote, visibleSelectedIds } =
    useNotesSelection(filteredNotes);

  const exportNotes = () => {
    if (visibleSelectedIds.size > 50) {
      dispatch(
        showAlertWithAction(
          "Too many notes selected for export. Please reduce the selection and try again (max: 50).",
        ),
      );

      return;
    }

    history.push(
      `/app/patient/${patientId}/export_notes?noteIds=${[...visibleSelectedIds].join(",")}`,
    );
  };

  return (
    <>
      <DashboardCard2
        Actions={
          <div className="tw:flex tw:gap-5">
            <Button2 isDisabled={!isDirty} onPress={clearAll} variant="text">
              Clear Filters
            </Button2>

            {isAuthorizedToExportNotes && (
              <Button2
                isDisabled={visibleSelectedIds.size === 0}
                onPress={exportNotes}
                variant="secondary"
              >
                Export
              </Button2>
            )}

            <NewNoteModal
              isAuthorizedToCreate={isAuthorizedToCreateNotes}
              sessionCount={notes?.length}
            />
          </div>
        }
        classNameHeader="tw:border-b-0 tw:md:mb-0! tw:md:pb-0! tw:items-start"
        rawChildren
        title="Filter Notes"
      >
        <NoteFilters
          dateEnd={dateEnd}
          dateStart={dateStart}
          isAllSelected={isAllSelected}
          isAuthorizedToExportNotes={isAuthorizedToExportNotes}
          onDateEndChange={setDateEnd}
          onDateStartChange={setDateStart}
          onNoteTypeChange={setNoteType}
          onSearchChange={setSearch}
          onToggleAll={toggleAll}
          search={search}
        />
      </DashboardCard2>

      {isLoading && <NoteListSkeletons />}

      {filteredNotes.length ? (
        <NoteList
          notes={filteredNotes}
          onToggleNote={toggleNote}
          onViewNote={(note: Note) => {
            history.push(`/app/patient/${patientId}/note/${note.id}/v2`);
          }}
          selectedNoteIds={visibleSelectedIds}
          viewAs={viewAs}
        />
      ) : (
        <EmptyTable
          ctaProps={{ label: "Clear Filters", onPress: clearAll }}
          message="No notes match the selected filters"
        />
      )}
    </>
  );
}
